import { tokens } from "../../../theme";
import { Box, Checkbox, Tooltip, Typography, useTheme } from "@mui/material";
import { githubUrl } from "../../../utils/urls";
import RedirectLink from "../../../components/RedirectLink";
import { releaseStyles } from "../styles";
import { commitKey, commitMessageText, isCommitDouble } from "../patchHelpers";

// One row: version, message and either a checkbox + build status (interactive,
// step 1) or a LEFT OUT chip in place of both (readOnly, the review step).
const CommitRow = ({
  commit,
  commitIndex,
  repoName,
  readOnly,
  leftOut,
  checked,
  onToggle,
}) => {
  const theme = useTheme();
  const { patchDrawer } = releaseStyles(tokens(theme.paletteId));
  return (
    <Box sx={readOnly ? patchDrawer.commitRowReadOnly : patchDrawer.commitRow}>
      {readOnly ? null : (
        <Checkbox
          size="small"
          checked={checked}
          sx={patchDrawer.commitCheckbox}
          inputProps={{
            "aria-label": `Include ${commit?.commit_tag} in this patch`,
          }}
          onChange={() => onToggle?.(repoName, commitIndex)}
        />
      )}
      <Tooltip title={<Typography>{commit?.commit_tag}</Typography>}>
        <Typography
          sx={
            leftOut
              ? {
                  ...patchDrawer.commitVersion,
                  ...patchDrawer.commitVersionLeftOut,
                }
              : patchDrawer.commitVersion
          }
        >
          {commit?.commit_tag}
        </Typography>
      </Tooltip>
      <Box
        sx={
          leftOut
            ? {
                ...patchDrawer.commitMessage,
                ...patchDrawer.commitMessageLeftOut,
              }
            : patchDrawer.commitMessage
        }
      >
        {isCommitDouble(commit) ? (
          <Box component="span" sx={patchDrawer.commitDoubleTag}>
            Double
          </Box>
        ) : null}
        <RedirectLink
          href={`${githubUrl}/${repoName}/commit/${commit?.commit_hash}`}
        >
          {commitMessageText(commit?.commit_message)}
        </RedirectLink>
      </Box>
      {leftOut ? (
        <Box component="span" sx={patchDrawer.leftOutTag}>
          LEFT OUT
        </Box>
      ) : (
        <Box
          component="span"
          sx={patchDrawer.commitStatus(commit?.build_status)}
        >
          {commit?.build_status}
        </Box>
      )}
    </Box>
  );
}; // One row per commit: a selection box, version, message and build status. Double
// commits carry a per-row DOUBLE tag, so the "Double Commit:" prefix is stripped
// from the message text and it reads as just the change.
//
// Commits are listed newest first and ship as a contiguous slice from the head
// down, so the first `skippedCount` rows are the excluded ones. Unticking a row
// skips it and everything above it; re-ticking brings it and everything below
// it back.
//
// `readOnly` is the create-patch review step: no checkbox, and included commits
// render first with the left-out ones after (struck through, LEFT OUT chip),
// using the same `commits.slice(skippedCount)` split the payload ships from —
// see getPatchPayload in PatchLabelDialog/index.jsx. `hideLeftOut` drops the
// left-out rows from a readOnly list entirely; the service header keeps its own
// count regardless.
const CommitsList = ({
  commits,
  repoName,
  skippedCount = 0,
  readOnly = false,
  hideLeftOut = false,
  onToggle,
}) => {
  if (!readOnly) {
    return (
      <>
        {commits?.map((commit, commitIndex) => (
          <CommitRow
            key={commitKey(repoName, commit) || commitIndex}
            commit={commit}
            commitIndex={commitIndex}
            repoName={repoName}
            checked={commitIndex >= skippedCount}
            onToggle={onToggle}
          />
        ))}
      </>
    );
  }

  const included = (commits || []).slice(skippedCount);
  const leftOut = hideLeftOut ? [] : (commits || []).slice(0, skippedCount);

  return (
    <>
      {included.map((commit, index) => (
        <CommitRow
          key={commitKey(repoName, commit) || `included-${index}`}
          commit={commit}
          repoName={repoName}
          readOnly
        />
      ))}
      {leftOut.map((commit, index) => (
        <CommitRow
          key={commitKey(repoName, commit) || `left-out-${index}`}
          commit={commit}
          repoName={repoName}
          readOnly
          leftOut
        />
      ))}
    </>
  );
};

export default CommitsList;
